import { z } from 'zod';
import { db, META, type ImportBatch, type PredictionLog, type LocalHousehold } from '@/db/db';
import { enqueueOp } from '@/db/repo';
import { BabyRecordSchema, EventRecordSchema, MAX_FUTURE_SKEW_MS, fromEventRecord, toBabyRecord, toEventRecord, type BabyRecord, type EventRecord } from '@/lib/schemas';
import { isValidTimeZone } from '@/lib/time';

/** Recoverable: records (including tombstones/audit provenance), import receipts,
 * prediction history, formula warning and active selection. Household name is
 * informational. Excludes auth/session, device/cursors, server versions, outbox,
 * sync errors/states and temporary Undo snapshots. SHA-256 detects damage, not
 * authenticity; backups contain private family data and are not encrypted.
 * Restore is an additive offline-only merge; household ownership is remapped,
 * existing preferences/name/selection win. Cloud targets must use cloud recovery.
 * Tombstones remain local audit records: no remote deletion is manufactured.
 */
export const MAX_BACKUP_BYTES = 25 * 1024 * 1024;
const iso = z.string().datetime({ offset: true });
const id = z.string().uuid();
const count = z.number().int().nonnegative();
const BatchSchema = z.object({ id, household_id: id, baby_id: id, filename: z.string().max(255), file_hash: z.string().min(1).max(128), started_at: iso, finished_at: iso, accepted: count, skipped: count, rejected: count }).strict();
const PredictionSchema = z.object({ id: z.string().min(1).max(512), baby_id: id, prediction_type: z.enum(['feed', 'sleep_start', 'wake']), model_version: z.string().min(1).max(128), generated_at: iso, generated_ms: z.number().finite(), basis_event_id: id, sample_size: count, window_start: iso, window_end: iso, point: iso, observed_at: iso.nullable(), error_minutes: z.number().finite().nullable(), within_window: z.boolean().nullable() }).strict();
const ImportMarkerSchema = z.object({ baby_id: id, source_system: z.literal('baby_tracker_csv'), source_key: z.string().regex(/^[a-f0-9]{64}$/), batch_id: id }).strict();
const DataSchema = z.object({
  household: z.object({ id, name: z.string().min(1).max(80) }).strict(),
  babies: z.array(BabyRecordSchema), events: z.array(EventRecordSchema),
  importBatches: z.array(BatchSchema), predictionLog: z.array(PredictionSchema),
  // Optional for compatibility with earlier version-1 backups. Keeps deliberately
  // removed imported rows from being resurrected by a subsequent ZIP import.
  importMarkers: z.array(ImportMarkerSchema).optional(),
  preferences: z.object({ formulaWarningMl: z.number().finite().nonnegative().optional(), activeBabyId: id.nullable() }).strict(),
}).strict();
const EnvelopeSchema = z.object({ format: z.literal('kai-household-backup'), version: z.literal(1), exported_at: iso, data: DataSchema, integrity: z.object({ algorithm: z.literal('SHA-256'), hash: z.string().regex(/^[a-f0-9]{64}$/) }).strict() }).strict();
export type HouseholdBackup = z.infer<typeof EnvelopeSchema>;
type Data = HouseholdBackup['data'];

/** Canonical JSON makes hashes and record comparisons independent of key order. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object') return `{${Object.entries(value).filter(([, v]) => v !== undefined).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  return JSON.stringify(value);
}
async function digest(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonical(value));
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
}
function fail(message: string): never { throw new Error(message); }
function unique(rows: { id: string }[], label: string) {
  if (new Set(rows.map(r => r.id)).size !== rows.length) fail(`Duplicate ${label} IDs in backup.`);
}
function validateData(data: Data) {
  unique(data.babies, 'baby'); unique(data.events, 'event'); unique(data.importBatches, 'import batch'); unique(data.predictionLog, 'prediction');
  const babies = new Map(data.babies.map(b => [b.id, b]));
  const events = new Map(data.events.map(e => [e.id, e]));
  if (data.events.some(e => babies.has(e.id))) fail('Duplicate entity IDs across profiles and events.');
  for (const b of data.babies) {
    if (b.household_id !== data.household.id || b.version !== 0 || !isValidTimeZone(b.timezone)) fail('Invalid baby ownership, version or timezone.');
  }
  const active = new Set<string>(); const sources = new Set<string>();
  for (const e of data.events) {
    const baby = babies.get(e.baby_id);
    if (e.household_id !== data.household.id || !baby || e.version !== 0) fail('Broken event ownership or baby reference.');
    if (Date.parse(e.started_at) > Date.now() + MAX_FUTURE_SKEW_MS) fail('Event start is too far in the future.');
    if (!e.deleted_at && baby.deleted_at) fail('Live event refers to a deleted baby.');
    if (e.source_key !== null) {
      const key = canonical([e.baby_id, e.source_system, e.source_key]);
      if (sources.has(key)) fail('Duplicate event provenance key.'); sources.add(key);
    }
    if (!e.deleted_at && e.type === 'sleep' && e.ended_at === null) {
      if (active.has(e.baby_id)) fail('Conflicting active sleep sessions.'); active.add(e.baby_id);
    }
  }
  for (const batch of data.importBatches) if (batch.household_id !== data.household.id || !babies.has(batch.baby_id) || Date.parse(batch.finished_at) < Date.parse(batch.started_at)) fail('Broken import batch reference or dates.');
  const markerKeys = new Set<string>();
  for (const marker of data.importMarkers ?? []) {
    if (!babies.has(marker.baby_id) || !data.importBatches.some(b => b.id === marker.batch_id && b.baby_id === marker.baby_id)) fail('Broken import marker reference.');
    const key = `${marker.baby_id}:${marker.source_key}`;
    if (markerKeys.has(key)) fail('Duplicate import marker.');
    markerKeys.add(key);
  }
  for (const p of data.predictionLog) {
    if (!babies.has(p.baby_id) || events.get(p.basis_event_id)?.baby_id !== p.baby_id || p.generated_ms !== Date.parse(p.generated_at) || Date.parse(p.window_start) > Date.parse(p.window_end) || Date.parse(p.point) < Date.parse(p.window_start) || Date.parse(p.point) > Date.parse(p.window_end)) fail('Broken prediction reference or dates.');
  }
  if (data.preferences.activeBabyId && (!babies.has(data.preferences.activeBabyId) || babies.get(data.preferences.activeBabyId)!.deleted_at)) fail('Invalid active baby reference.');
}
function tables() { return [db.babies, db.events, db.meta, db.importBatches, db.predictionLog, db.outbox]; }
function portableBaby(b: BabyRecord): BabyRecord { return { ...BabyRecordSchema.parse(b), version: 0 }; }
function portableEvent(e: EventRecord): EventRecord { return { ...EventRecordSchema.parse(e), version: 0 }; }

export async function exportHouseholdBackup(): Promise<string> {
  // Hash only after the consistent IndexedDB snapshot has closed.
  const data = await db.transaction('r', tables(), async (): Promise<Data> => {
    const household = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    if (!household) fail('Create a local household before exporting.');
    const babies = await db.babies.where('household_id').equals(household.id).toArray();
    const babyIds = new Set(babies.map(b => b.id));
    const settings = (await db.meta.get(META.settings))?.value as { formulaWarningMl?: number } | undefined;
    const active = (await db.meta.get(META.activeBabyId))?.value as string | undefined;
    const importMarkers = (await db.meta.toArray()).filter(row => row.key.startsWith('importSource:')).flatMap(row => {
      const marker = row.value as Record<string, unknown>;
      if (!marker || marker.household_id !== household.id || !babyIds.has(marker.baby_id as string)) return [];
      return [ImportMarkerSchema.parse({ baby_id: marker.baby_id, source_system: marker.source_system, source_key: marker.source_key, batch_id: marker.batch_id })];
    });
    return { household: { id: household.id, name: household.name }, babies: babies.map(b => portableBaby(toBabyRecord(b))), events: (await db.events.where('household_id').equals(household.id).toArray()).map(e => portableEvent(toEventRecord(e))), importBatches: (await db.importBatches.toArray()).filter(b => b.household_id === household.id), predictionLog: (await db.predictionLog.toArray()).filter(p => babyIds.has(p.baby_id)), importMarkers, preferences: { ...(settings?.formulaWarningMl !== undefined ? { formulaWarningMl: settings.formulaWarningMl } : {}), activeBabyId: active && babies.some(b => b.id === active && !b.deleted_at) ? active : null } };
  });
  const validated = DataSchema.parse(data); validateData(validated);
  const body = { format: 'kai-household-backup' as const, version: 1 as const, exported_at: new Date().toISOString(), data: validated };
  return JSON.stringify({ ...body, integrity: { algorithm: 'SHA-256', hash: await digest(body) } }, null, 2);
}
export async function validateBackup(text: string): Promise<HouseholdBackup> {
  if (text.length > MAX_BACKUP_BYTES || new TextEncoder().encode(text).length > MAX_BACKUP_BYTES) fail('Backup exceeds the 25 MB limit.');
  let raw: unknown;
  try { raw = JSON.parse(text); } catch { fail('Invalid or truncated backup JSON.'); }
  const parsed = EnvelopeSchema.safeParse(raw);
  if (!parsed.success) fail('Unsupported version or malformed backup records.');
  const backup = parsed.data;
  // Reject unknown event fields and omitted schema defaults, rather than silently dropping data.
  if (canonical(raw) !== canonical(backup)) fail('Malformed or unexpected backup fields.');
  const { integrity, ...body } = backup;
  if (await digest(body) !== integrity.hash) fail('Backup integrity check failed. The file may be damaged or changed.');
  validateData(backup.data);
  return backup;
}
export interface RestorePreview {
  hash: string; targetHouseholdId: string; sourceHouseholdName: string; remapsHousehold: boolean;
  babies: number; events: number; importBatches: number; predictionLog: number; importMarkers: number; duplicates: number; tombstones: number;
  restoreFormulaWarning: boolean; restoreActiveBaby: boolean;
}
export type RestoreResult = RestorePreview;

async function plan(backup: HouseholdBackup) {
  const target = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
  if (!target || target.cloud !== false) fail('Restore requires a current offline household. Cloud households must use cloud recovery.');
  const data = backup.data;
  const babies = data.babies.map(b => ({ ...b, household_id: target.id }));
  const events = data.events.map(e => ({ ...e, household_id: target.id }));
  const batches = data.importBatches.map(b => ({ ...b, household_id: target.id }));
  const [allBabies, allEvents, allBatches, allPredictions, settings, active] = await Promise.all([db.babies.toArray(), db.events.toArray(), db.importBatches.toArray(), db.predictionLog.toArray(), db.meta.get(META.settings), db.meta.get(META.activeBabyId)]);
  const existingBabyIds = new Set(allBabies.map(b => b.id));
  const existingEventIds = new Set(allEvents.map(e => e.id));
  if (events.some(e => existingBabyIds.has(e.id)) || babies.some(b => existingEventIds.has(b.id))) fail('Entity ID collides across profiles and events.');
  let duplicates = 0;
  function additions<T extends { id: string }>(incoming: T[], existing: T[]): T[] {
    const byId = new Map(existing.map(r => [r.id, r]));
    return incoming.filter(row => {
      const old = byId.get(row.id);
      if (!old) return true;
      if (canonical(old) !== canonical(row)) fail(`Divergent record collision: ${row.id}. Existing data was preserved.`);
      duplicates++; return false;
    });
  }
  const newBabies = additions(babies, allBabies.map(b => portableBaby(toBabyRecord(b))));
  const newEvents = additions(events, allEvents.map(e => portableEvent(toEventRecord(e))));
  const newBatches = additions(batches, allBatches);
  const newPredictions = additions(data.predictionLog, allPredictions);
  const newMarkers: NonNullable<Data['importMarkers']> = [];
  for (const marker of data.importMarkers ?? []) {
    const old = (await db.meta.get(`importSource:${marker.baby_id}:${marker.source_key}`))?.value as Record<string, unknown> | undefined;
    if (old) {
      if (old.household_id !== target.id || old.baby_id !== marker.baby_id || old.source_system !== marker.source_system || old.source_key !== marker.source_key) fail('Import marker ownership collision.');
      duplicates++;
    } else newMarkers.push(marker);
  }
  // Provenance aliases must not silently reintroduce the same imported row.
  const existingSources = new Set(allEvents.filter(e => e.source_key !== null).map(e => canonical([e.baby_id, e.source_system, e.source_key])));
  for (const e of newEvents) if (e.source_key !== null && existingSources.has(canonical([e.baby_id, e.source_system, e.source_key]))) fail('Imported source key collides with an existing event.');
  const mergedBabies = [...allBabies, ...newBabies];
  const mergedBabyMap = new Map(mergedBabies.filter(b => b.household_id === target.id).map(b => [b.id, b]));
  const mergedEvents = [...allEvents, ...newEvents];
  const running = new Set<string>();
  for (const e of mergedEvents.filter(e => e.household_id === target.id)) {
    const baby = mergedBabyMap.get(e.baby_id);
    if (!baby || (!e.deleted_at && baby.deleted_at)) fail('Broken merged baby reference.');
    if (!e.deleted_at && e.type === 'sleep' && e.ended_at === null) {
      if (running.has(e.baby_id)) fail('Conflicting active sleep sessions.'); running.add(e.baby_id);
    }
  }
  // Orphaned pending writes can carry prior remote identity; never coalesce into them.
  for (const row of [...newBabies, ...newEvents]) if (await db.outbox.where('entity_id').equals(row.id).count()) fail('Existing pending operation collides with a restored record.');
  const preview: RestorePreview = { hash: backup.integrity.hash, targetHouseholdId: target.id, sourceHouseholdName: data.household.name, remapsHousehold: target.id !== data.household.id, babies: newBabies.length, events: newEvents.length, importBatches: newBatches.length, predictionLog: newPredictions.length, importMarkers: newMarkers.length, duplicates, tombstones: [...newBabies, ...newEvents].filter(r => r.deleted_at).length, restoreFormulaWarning: settings === undefined && data.preferences.formulaWarningMl !== undefined, restoreActiveBaby: !mergedBabies.some(b => b.id === active?.value && b.household_id === target.id && !b.deleted_at) && data.preferences.activeBabyId !== null };
  return { preview, newBabies, newEvents, newBatches, newPredictions, newMarkers };
}
export async function previewRestore(text: string): Promise<RestorePreview> {
  const backup = await validateBackup(text);
  return db.transaction('r', tables(), async () => (await plan(backup)).preview);
}
export async function restoreBackup(text: string, confirmed: RestorePreview): Promise<RestoreResult> {
  const backup = await validateBackup(text);
  // No WebCrypto, file reads or other external async work in this write transaction.
  return db.transaction('rw', tables(), async () => {
    const p = await plan(backup);
    if (canonical(p.preview) !== canonical(confirmed)) fail('Restore preview changed. Preview again before confirming.');
    for (const b of p.newBabies) {
      await db.babies.add({ ...b, sync_state: 'local', sync_error: null });
      if (!b.deleted_at) await enqueueOp({ household_id: b.household_id, entity: 'baby', entity_id: b.id, op: 'create', data: b, currentServerVersion: 0 });
    }
    for (const e of p.newEvents) {
      await db.events.add(fromEventRecord(e, 'local'));
      if (!e.deleted_at) await enqueueOp({ household_id: e.household_id, entity: 'event', entity_id: e.id, op: 'create', data: e, currentServerVersion: 0 });
    }
    for (const b of p.newBatches) await db.importBatches.add(b as ImportBatch);
    for (const pLog of p.newPredictions) await db.predictionLog.add(pLog as PredictionLog);
    for (const marker of p.newMarkers) await db.meta.put({ key: `importSource:${marker.baby_id}:${marker.source_key}`, value: { ...marker, household_id: p.preview.targetHouseholdId } });
    if (p.preview.restoreFormulaWarning) await db.meta.put({ key: META.settings, value: { formulaWarningMl: backup.data.preferences.formulaWarningMl } });
    if (p.preview.restoreActiveBaby) await db.meta.put({ key: META.activeBabyId, value: backup.data.preferences.activeBabyId });
    return p.preview;
  });
}
